import { useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useCallback } from "react";
import { toast } from "sonner";

import { TwilioCallStatus, normalizeTwilioStatus } from "@/constants/twilio-call-status";
import { getCall } from "@/hooks/queries/call.queries";
import { CallFailureType } from "@/types/call.types";

type UseHandleCallFailureOptions = {
    onActionableAudioIssue?: () => void;
};

export function useHandleCallFailure(
    t: TFunction,
    userHungUpRef: React.MutableRefObject<boolean>,
    options?: UseHandleCallFailureOptions
) {
    const queryClient = useQueryClient();

    const notifyAudioDeviceIssue = useCallback(() => {
        options?.onActionableAudioIssue?.();
    }, [options]);

    return useCallback(
        async (failureType: CallFailureType, callId?: string) => {
            if (failureType === CallFailureType.AUDIO_DEVICE) {
                userHungUpRef.current = false;
                toast.error(t("call_failed_generic"), {
                    description: t("call_failed_generic_description"),
                });
                notifyAudioDeviceIssue();
                return;
            }

            if (callId) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
                try {
                    const call = await queryClient.fetchQuery({
                        queryKey: ["call", callId],
                        queryFn: () => getCall(callId),
                    });
                    const status = normalizeTwilioStatus(call.status);
                    if (status === TwilioCallStatus.NO_ANSWER && userHungUpRef.current) {
                        userHungUpRef.current = false;
                        return;
                    }
                    if (failureType === CallFailureType.INVALID_NUMBER) {
                        toast.error(t("phone_number_is_fake"));
                        return;
                    }
                    userHungUpRef.current = false;
                    switch (status) {
                        case TwilioCallStatus.BUSY:
                            toast.error(t("call_failed_busy"), { description: t("call_failed_busy_description") });
                            return;
                        case TwilioCallStatus.NO_ANSWER:
                            toast.error(t("call_failed_no_answer"), {
                                description: t("call_failed_no_answer_description"),
                            });
                            return;
                        case TwilioCallStatus.FAILED:
                            toast.error(t("call_failed_generic"), {
                                description: t("call_failed_generic_description"),
                            });
                            return;
                        case TwilioCallStatus.COMPLETED:
                        case TwilioCallStatus.CANCELED:
                            return;
                        default:
                            toast.error(t("call_failed_generic"), {
                                description: t("call_failed_generic_description"),
                            });
                            return;
                    }
                } catch (err) {
                    userHungUpRef.current = false;
                    console.error("Error fetching call status from backend:", err);
                    toast.error(t("call_failed_generic"), {
                        description: t("call_failed_generic_description"),
                    });
                    return;
                }
            }
            userHungUpRef.current = false;
            toast.error(t("call_failed_generic"), {
                duration: 4000,
                description: t("call_failed_generic_description"),
            });
        },
        [notifyAudioDeviceIssue, queryClient, t]
    );
}
